import React, { Ref, useCallback, useImperativeHandle, useRef } from 'react';
import { ListOnItemsRenderedProps } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { Box } from '@tanium/react-box';
import { DynamicList, DynamicListHandles, DynamicListProps } from '@tanium/react-dynamic-list';
import { aggregateRefs } from '@tanium/react-use-aggregated-ref';
import { useDimensions } from '@tanium/react-use-dimensions';
import { COLUMN_HEADER_HEIGHT } from '../../constants';
import { useListOverflow } from '../../hooks/useListOverflow';
import { Id } from '../../types/Id';
import { RibbonListVirtualizedProps } from '../../types/StackedRibbonListComponentTypes';
import { ItemRenderer } from '../Item/ItemRenderer';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { ListProps } from './ListProps';
import { RibbonListVirtualizedHandles } from './RibbonListHandles';
import { DEFAULT_ITEMS_WHILE_LOADING } from './useRibbonListWrapper';

const overscanCount = 5;

interface Props<TId extends Id, TItem>
  extends ListProps<TId, TItem>,
    RibbonListVirtualizedProps<TId, TItem> {}

/**
 * A component that renders the main body of a StackedRibbonList using DOM virtualization. This
 * component should be rendered inside a RibbonListWrapper.
 */
const RibbonListVirtualizedInner = <TId extends Id, TItem>(
  {
    estimatedItemSize = 30,
    initialScrollOffset,
    isItemLoaded,
    itemCount,
    itemIdGetter,
    loadMoreItems,
    minimumBatchSize = 25,
    onListOverflowChange,
    onScroll,
    isLoading,
    isError,
    unstable_infiniteScrollItemCount,
  }: Props<TId, TItem>,
  ref: React.Ref<RibbonListVirtualizedHandles>,
) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const infiniteLoader = useRef<InfiniteLoader>(null);

  const innerRef = useListOverflow(outerRef, onListOverflowChange);
  const dynamicListRef = useRef<DynamicListHandles | null>(null);

  const loadMoreItemsInner = useCallback(
    (startIndex: number, stopIndex: number) => {
      loadMoreItems?.(startIndex, stopIndex);
      return Promise.resolve();
    },
    [loadMoreItems],
  );

  const [resizeRef, { height = 0, width = 0 }] = useDimensions({ height: true, width: true });

  const scrollToOffset = useCallback((offset: number) => {
    dynamicListRef.current?.scrollToOffset(offset);
  }, []);

  useImperativeHandle(ref, () => ({
    refreshList: () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      infiniteLoader.current!.resetloadMoreItemsCache();
    },
    scrollToOffset,
    scrollToTop: () => {
      scrollToOffset(0);
    },
    scrollNeighboringItemIntoViewIfNecessary: (index: number) => {
      dynamicListRef.current?.scrollNeighboringItemIntoViewIfNecessary(index);
    },
  }));

  const displayedItemCount =
    unstable_infiniteScrollItemCount !== undefined && unstable_infiniteScrollItemCount < itemCount
      ? Math.min(unstable_infiniteScrollItemCount + DEFAULT_ITEMS_WHILE_LOADING, itemCount)
      : itemCount;

  const infiniteLoaderChild = useCallback(
    ({
      onItemsRendered,
      ref: loaderRef,
    }: {
      onItemsRendered: (props: ListOnItemsRenderedProps) => unknown;
      ref: Ref<DynamicListHandles>;
    }) => {
      const aggregatedRef = aggregateRefs([loaderRef, dynamicListRef]);

      return (
        <Box height="100%" width="100%" ref={resizeRef} role="rowgroup" position="relative">
          <Box position="absolute" role="presentation">
            <DynamicList
              role="presentation"
              ref={aggregatedRef}
              height={height - COLUMN_HEADER_HEIGHT}
              width={width}
              estimatedItemSize={estimatedItemSize}
              itemCount={displayedItemCount}
              overscanCount={overscanCount}
              itemKey={itemIdGetter}
              onItemsRendered={onItemsRendered}
              onScroll={onScroll}
              deferInitialScroll={isLoading}
              initialScrollOffset={initialScrollOffset}
              innerElementType={itemCount === 0 ? EmptyState : 'div'}
              outerRef={outerRef}
              innerRef={innerRef}
            >
              {ItemRenderer as DynamicListProps['children']}
            </DynamicList>
          </Box>
        </Box>
      );
    },
    [
      estimatedItemSize,
      displayedItemCount,
      height,
      initialScrollOffset,
      innerRef,
      isLoading,
      itemCount,
      itemIdGetter,
      onScroll,
      resizeRef,
      width,
    ],
  );

  if (isError && !isLoading) {
    return <ErrorState />;
  }

  return (
    <InfiniteLoader
      ref={infiniteLoader}
      isItemLoaded={isItemLoaded}
      itemCount={displayedItemCount}
      loadMoreItems={loadMoreItemsInner}
      minimumBatchSize={minimumBatchSize}
    >
      {infiniteLoaderChild}
    </InfiniteLoader>
  );
};

export const RibbonListVirtualized = React.forwardRef(RibbonListVirtualizedInner);
